import { useEffect, useRef } from 'react';
import { warnOnce } from '@/lib/warnOnce';
import { connectSignageMqtt } from '@/mqtt/client';
import type { BrokerCredentials } from '@/lib/localBroker';
import { useConnectGate, useSignageStore } from '@/store/signageStore';

/**
 * The half of the connect lifecycle that is identical on a display and against a local
 * broker: open the client once `canConnectMqtt` is set, and say something when a config can
 * never get there.
 *
 * Only *how the credentials are obtained* differs between the two providers — a preventer
 * token and a `savemeta` round trip on a display (`LiveMqttProvider`), two environment
 * variables and no round trip locally (`LocalBrokerProvider`). Everything from
 * `setCanConnectMqtt()` onwards is the same engine with the same options, which is the
 * property that makes local-broker mode worth having: what it exercises is what a display
 * runs, not a parallel implementation of it.
 *
 * Extracted from `LiveMqttProvider` rather than duplicated for exactly that reason. It runs
 * outside render and reads the store imperatively — see `MqttProvider` for why.
 */
export function useMqttConnection(credentials?: BrokerCredentials): void {
  const gate = useConnectGate();
  const canConnectMqtt = useSignageStore((s) => s.canConnectMqtt);
  const connectStarted = useRef(false);

  /* ---- access granted -> connect ------------------------------------------------------- */

  useEffect(() => {
    if (!canConnectMqtt) return;
    if (connectStarted.current) return;

    const { mqttUrl, token, baseTopic, floorCode, heartBeat, topicItems } =
      useSignageStore.getState();

    if (!mqttUrl) {
      // `loadConfig` already warned; this is the point where it stops being cosmetic.
      console.error(
        '[mqtt] no broker url — the config has no `mqttUrl` and VITE_MQTT_URL is unset. ' +
          'This display will show placeholder data forever.',
      );
      return;
    }

    connectStarted.current = true;
    const handle = connectSignageMqtt({
      url: mqttUrl,
      token,
      credentials,
      baseTopic,
      floorCode,
      heartBeatMs: heartBeat,
      // Snapshot: this is the list the broker was asked to authorise, so it is the list we
      // subscribe with. `resolveComponentId` below stays live for routing.
      topics: [...topicItems.keys()],
      resolveComponentId: (topic) => useSignageStore.getState().topicItems.get(topic),
      deliver: (componentId, payload) => {
        useSignageStore.getState().deliverMessage(componentId, payload);
      },
      onConnectedChange: (connected) => {
        useSignageStore.getState().setMqttConnected(connected);
      },
      reload: () => {
        window.location.reload();
      },
    });

    return () => {
      handle.stop();
      connectStarted.current = false;
    };
    // `credentials` is a build-time constant in both callers, so it never invalidates this
    // effect; listing it keeps the lint rule honest without changing behaviour.
  }, [canConnectMqtt, credentials]);

  /* ---- the config that can never connect ----------------------------------------------- */

  /**
   * A config whose widgets are all static (`DateWidget`, `Logo`, `ImageLabelWidget`,
   * `ProgressBar`) registers no topics, so the gate never opens and nothing ever connects.
   * That is correct — there is nothing to subscribe to — but from the outside it is
   * indistinguishable from a hang, so it says so once.
   */
  const noTopics = gate.moduleLoaded && gate.topicCount === 0;
  useEffect(() => {
    if (!noTopics) return;
    warnOnce(
      'mqtt-no-topics',
      '[mqtt] no topics registered; not connecting. Every widget in this config is static — ' +
        'if that is a surprise, check `options.topic` in the config.',
    );
  }, [noTopics]);
}
